import {
  faArrowLeft,
  faBan,
  faSave,
  faShare,
  faTimes,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import FolderGroupSelect from 'components/common/FolderGroupSelect';
import IconButton from 'components/common/IconButton';
import Editor from 'components/editor/Editor';
import DialogModal from 'components/modals/DialogModal';
import KbFolderRegistrationModal from 'components/modals/KbFolderRegistrationModal';
import TagSearch from 'components/tags/TagSearch';
import useNotification from 'hooks/useNotification';
import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row
} from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import KbArticlesService from 'services/KbArticles.service';
import KbGroupsService from 'services/KbGroups.service';

const ArticleEdit = () => {
  const { id } = useParams();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const [articleData, setArticleData] = useState({
    title: '',
    content: '',
    tags: [],
    kbFolder: { id: '' }
  });

  const [validation, setValidation] = useState({
    title: true,
    content: true
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [groups, setGroups] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    findArticle();
  }, [id]);

  useEffect(() => {
    getGroups();
  }, []);

  useEffect(() => {
    if (articleData.status?.status === 'PUBLISHED') {
      setIsPublished(true);
    }
  }, [articleData.status]);

  const findArticle = async () => {
    const response = await KbArticlesService.find(id);
    if (response.id) {
      const body = {
        title: response.title || '',
        content: response.content || '',
        kbFolder: { id: response.kbFolder?.id || '' },
        tags: response.tags.map(tag => tag.name) || []
      };

      setArticleData(body);

      if (response.status?.status === 'PUBLISHED') {
        setIsPublished(true);
      }
    }
  };

  const getGroups = async () => {
    try {
      const response = await KbGroupsService.listWithFolders();
      if (!response.userMessage) {
        setGroups(response);
      }
    } catch (error) {
      console.error('Erro ao buscar grupos:', error);
    }
  };

  // Função para atualizar o conteúdo e validação
  const handleSetBody = (isValid, field, value) => {
    setArticleData({
      ...articleData,
      [field]: value
    });

    setValidation({
      ...validation,
      [field]: isValid
    });
  };

  const handleInputChange = (field, value) => {
    setArticleData({
      ...articleData,
      [field]: value
    });
  };

  // Funções para gerenciar tags
  const handleAddTag = tag => {
    if (tag.trim() !== '' && !articleData.tags.includes(tag.trim())) {
      setArticleData({
        ...articleData,
        tags: [...articleData.tags, tag.trim()]
      });
    }
  };

  const handleRemoveTag = tag => {
    setArticleData({
      ...articleData,
      tags: articleData.tags.filter(t => t !== tag)
    });
  };

  // Funções para salvar
  const handleSave = async e => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Salvando artigo:', articleData);

    const response = await KbArticlesService.update(id, articleData);
    if (response.id) {
      showSuccessNotification('Artigo atualizado com sucesso');

      setTimeout(() => {
        navigate(`/documents/articles/${id}`);
      }, 1000);
    } else {
      showErrorNotification(
        response.userMessage || 'Erro ao atualizar o artigo'
      );
    }
  };

  const publish = async e => {
    e.preventDefault();
    e.stopPropagation();
    const req = await KbArticlesService.publish(id);
    if (req.ok) {
      showSuccessNotification('Artigo publicado com sucesso');
      setIsPublished(true);
    } else {
      showErrorNotification(req.userMessage || 'Erro ao publicar o artigo');
    }
  };

  const unpublish = async e => {
    e.preventDefault();
    e.stopPropagation();

    const req = await KbArticlesService.unpublish(id);
    if (req.ok) {
      setIsPublished(false);
      showSuccessNotification('Artigo despublicado com sucesso');
    } else {
      showErrorNotification(req.userMessage || 'Erro ao despublicar o artigo');
    }
  };

  const handleDelete = async response => {
    if (!response) {
      setShowDeleteModal(false);
      return;
    }

    const req = await KbArticlesService.delete(id);
    if (req.ok) {
      showSuccessNotification('Artigo excluído com sucesso');
      setTimeout(() => {
        navigate('/documents');
      }, 500);
    } else {
      showErrorNotification(req.userMessage || 'Erro ao excluir o artigo');
    }
  };

  const handleCloseFolderModal = () => {
    setShowFolderModal(false);
  };

  return (
    <>
      <KbFolderRegistrationModal
        showModal={showFolderModal}
        handleClose={handleCloseFolderModal}
        isSuccess={() => {}}
        groups={groups}
      />

      {/* SINGLE CARD WITH ALL CONTENT */}
      <FalconComponentCard>
        <Container>
          <Card.Header
            as={Flex}
            alignItems="center"
            justifyContent="between"
            className="w-100"
          >
            <Flex alignItems={'center'}>
              <IconButton
                icon={faArrowLeft}
                as={Link}
                variant="falcon-default"
                size="sm"
                className="me-2"
                to={`/documents/articles/${id}`}
              />

              <h3 className="m-0">Editar Artigo</h3>
            </Flex>

            <Flex>
              <Button
                variant="falcon-danger"
                className="me-2"
                onClick={() => setShowDeleteModal(true)}
              >
                <FontAwesomeIcon icon={faTrash} className="me-1" /> Excluir
              </Button>
              {isPublished ? (
                <Button onClick={unpublish}>
                  <FontAwesomeIcon icon={faBan} className="me-1" />
                  Despublicar
                </Button>
              ) : (
                <Button onClick={publish}>
                  <FontAwesomeIcon icon={faShare} className="me-1" />
                  Publicar
                </Button>
              )}
            </Flex>
          </Card.Header>

          <FalconComponentCard.Body>
            <Form.Group className="mb-3" controlId="articleTitle">
              <Form.Label>
                Título <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Título"
                value={articleData.title}
                onChange={e => handleInputChange('title', e.target.value)}
                isInvalid={!validation.title}
              />
              <Form.Control.Feedback type="invalid">
                O título é obrigatório.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="articleContent">
              <Form.Label>
                Conteúdo <span className="text-danger">*</span>
              </Form.Label>
              <Editor
                data={articleData.content}
                value={value => handleSetBody(true, 'content', value)}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Insira conteúdo para o artigo.
              </Form.Control.Feedback>
            </Form.Group>

            {/* PROPRIEDADES DO ARTIGO abaixo do editor */}
            <Row>
              <Col lg={12}>
                <div className="mb-2 align-items-center">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Flex justifyContent="between" alignItems="center">
                          <Form.Label>
                            Pasta <span className="text-danger">*</span>
                          </Form.Label>
                          <Button
                            variant="link"
                            onClick={() => setShowFolderModal(true)}
                            className="p-0 text-decoration-none"
                          >
                            + Criar novo
                          </Button>
                        </Flex>
                        <FolderGroupSelect
                          groups={groups}
                          value={articleData.kbFolder.id}
                          onChange={folderId =>
                            setArticleData({
                              ...articleData,
                              kbFolder: { id: parseInt(folderId) }
                            })
                          }
                          placeholder="Selecionar pasta"
                          required={true}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="mb-2">Tags</Form.Label>
                        <TagSearch onAddTag={handleAddTag} />
                        <div className="mt-2">
                          {articleData.tags.map((tag, index) => (
                            <Badge
                              key={index}
                              bg="light"
                              text="dark"
                              className="me-1 p-2"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleRemoveTag(tag)}
                            >
                              {tag} &times;
                            </Badge>
                          ))}
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
            <Flex justifyContent="between" alignItems="center">
              <div>
                <Button
                  variant="falcon-danger"
                  onClick={() => setShowDeleteModal(true)}
                >
                  <FontAwesomeIcon icon={faTrash} className="me-1" /> Excluir
                </Button>
              </div>
              <Flex alignItems={'center'}>
                <Button
                  variant="falcon-default"
                  className="me-2"
                  onClick={() => navigate(-1)}
                >
                  <FontAwesomeIcon icon={faTimes} className="me-1" /> Cancelar
                </Button>

                <Button variant="primary" className="me-2" onClick={handleSave}>
                  <FontAwesomeIcon icon={faSave} className="me-1" /> Salvar
                </Button>
              </Flex>
            </Flex>
          </FalconComponentCard.Body>
        </Container>
      </FalconComponentCard>

      {/* Modal de confirmação de exclusão */}
      <DialogModal
        handleClose={() => setShowDeleteModal(false)}
        message={'Deseja excluir este artigo?'}
        showModal={showDeleteModal}
        response={handleDelete}
      />
    </>
  );
};

export default ArticleEdit;
